import { useCallback, useContext, useMemo, useReducer } from "react";

import { useCookies } from "react-cookie";

import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

import { BannerType } from "@dotnet/telematics-design-system";

import AlertContext from "context/alert/alertContext";
import LanguageContext from "context/language/languageContext";
import ParamsContext from "context/params/paramsContext";
import {
    COMPANY_FILTER,
    I18_NEXT_LANG,
    ROOT_COMPANY,
    TRUE,
    X_INTERNAL_ACCESS
} from "shared/constants/strings";
import { getCustomResourceLimits, getValidLanguage } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import {
    AUTHENTICATION_TYPES,
    COMPANY_LEVELS,
    COOKIE_NAMES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    FILTER_NAMES
} from "shared/types/enums";
import { AccessGroup, ResourceLimitSettings } from "shared/types/interfaces";

import {
    AUTH_ERROR,
    CHANGE_USER_COMPANY_LEVEL,
    CHANGE_USER_CUSTOM_RESOURCE_LIMITS,
    REMOVE_LOADING,
    REMOVE_MESSAGE,
    RESET_AUTH_TYPE,
    SET_COOKIE_CONSENT,
    SET_LOADING,
    SET_USER,
    TOGGLE_2FA_ENABLE,
    TOGGLE_2FA_VERIFICATION
} from "./authActions";
import AuthContext from "./authContext";
import AuthReducer from "./authReducer";
import { PERMISSION_GROUP_ARRAY } from "./constants";
import {
    getAllPermissions,
    getDefaultPermissionState,
    hasLanguageChangedManually
} from "./functions";
import {
    AuthStateProps,
    CompanyAttributes,
    LoginUserParameters,
    Message,
    PERMISSIONS,
    User,
    UserWithCompanyAttributes
} from "./types";

const AuthState = ({ children }: AuthStateProps) => {
    const { removeAlert } = useContext(AlertContext);
    const { setQueryParams } = useContext(ParamsContext);
    const { language, setNewLanguage } = useContext(LanguageContext);

    const [cookies] = useCookies([COOKIE_NAMES.Analytics]);

    const initialState = {
        user: null,
        permissions: getDefaultPermissionState(),
        accessGroups: [],
        hasSso: false,
        allCompanyLevelSettings: null,
        message: null,
        authType: null,
        is2FaSetupRequired: true,
        is2FaEnableFlowSeen: false,
        is2FaVerification: false,
        isAuthenticated: false,
        isSupportUser: false,
        isAdminUser: false,
        isAdminManagerUser: false,
        isInternalIntegrationUser: false,
        isSystemUser: false,
        isSuperUser: false,
        isCarrierUser: false,
        isLoading: false,
        isAuthLoading: true,
        canInviteUsers: false, // depends on root company (can change)
        cookieConsent: {
            [COOKIE_NAMES.Analytics]: String(cookies.analytics)
        }
    };

    const [state, dispatch] = useReducer(AuthReducer, initialState);

    const { postData, logout, getData, handleResponse, getCriticalMessage } =
        useApi();

    const clearQueryParams = useCallback(
        (onlyCompanies?: boolean) => {
            const params = onlyCompanies
                ? { [FILTER_NAMES.CompanyId]: [] }
                : {};

            setQueryParams({ params, removeFilterOptions: true });
            !onlyCompanies && sessionStorage.clear();
            localStorage.removeItem(ROOT_COMPANY);
            localStorage.removeItem(COMPANY_FILTER);
        },
        [setQueryParams]
    );

    const getUserCompanyAttributes = (
        userData: User,
        attributesData: CompanyAttributes
    ) => {
        const {
            company_level,
            company_type,
            password_auth_enabled,
            user_invites_enabled,
            root_client_company_id,
            customResourceLimitSettings
        } = attributesData;

        const userDataCopy: UserWithCompanyAttributes = {
            ...userData,
            company_level,
            company_type,
            password_auth_enabled,
            user_invites_enabled,
            root_client_company_id,
            customResourceLimitSettings
        };

        return userDataCopy;
    };

    const handleFailedResponse = useCallback(
        (error: any, hasSso?: boolean) => {
            const { message: newErrorMessage } = getCriticalMessage(error);

            dispatch({
                type: AUTH_ERROR,
                payload: {
                    message: {
                        status: BannerType.Error,
                        title: newErrorMessage
                    },
                    hasSso
                }
            });
        },
        [getCriticalMessage]
    );

    const handleSuccessResponse = useCallback(
        async (
            user: User,
            permissionList: PERMISSIONS[],
            accessGroups: AccessGroup[],
            hasSso: boolean,
            twoFactorSetupRequired: boolean
        ) => {
            const hasLngChanged = hasLanguageChangedManually();
            const languageFromStorage = localStorage.getItem(I18_NEXT_LANG);
            const userLng = user.language;

            const newLanguage = hasLngChanged
                ? getValidLanguage(languageFromStorage, userLng)
                : userLng;

            setNewLanguage(newLanguage, hasLngChanged, false, false);

            const endpoints = [
                getData(
                    `${ENDPOINTS.Companies}/${user.company_id}${ENDPOINT_PATH_SEGMENTS.Attributes}`
                ),
                getData(ENDPOINTS.CompanyLevels)
            ];

            try {
                const [
                    {
                        data: {
                            level,
                            password_auth_enabled,
                            user_invites_enabled,
                            root_client_company_id,
                            type
                        }
                    },
                    {
                        data: { company_level_settings }
                    }
                ] = await Promise.all(endpoints);

                const customResourceLimits = await getCustomResourceLimits(
                    user.company_id,
                    getData
                );

                const userWithCompanyAtrributes = getUserCompanyAttributes(
                    user,
                    {
                        company_level: level,
                        company_type: type,
                        password_auth_enabled,
                        user_invites_enabled,
                        root_client_company_id,
                        customResourceLimitSettings: customResourceLimits
                    }
                );

                Sentry.setUser({ email: user.email });

                dispatch({
                    type: SET_USER,
                    payload: {
                        user: userWithCompanyAtrributes,
                        companyLevelSettings: company_level_settings,
                        permissionList,
                        accessGroups,
                        hasSso,
                        twoFactorSetupRequired
                    }
                });
            } catch (error) {
                handleFailedResponse(error);
            }
        },
        [getData, setNewLanguage, handleFailedResponse]
    );

    const checkAuth = useCallback(async () => {
        let hasSso: boolean;

        try {
            setLoading();

            const {
                data: {
                    logged_in,
                    permissions,
                    access_groups,
                    two_factor_setup_required,
                    user
                },
                headers
            } = await getData(`${ENDPOINTS.Users}${ENDPOINT_PATH_SEGMENTS.Me}`);

            hasSso = headers[X_INTERNAL_ACCESS] === TRUE;

            if (logged_in) {
                handleSuccessResponse(
                    user,
                    permissions,
                    access_groups,
                    hasSso,
                    two_factor_setup_required
                );
            } else {
                const hasLngChanged = hasLanguageChangedManually();

                setNewLanguage(language, false, hasLngChanged, false);
                dispatch({ type: AUTH_ERROR, payload: { hasSso } });
                clearQueryParams(true);
            }
        } catch (error) {
            const err = error as AxiosError;

            const internalAccessHeader =
                err.response?.headers?.[X_INTERNAL_ACCESS];

            hasSso = String(internalAccessHeader) === TRUE;

            handleFailedResponse(error, hasSso);
        } finally {
            removeLoading();
        }
    }, [
        language,
        clearQueryParams,
        getData,
        setNewLanguage,
        handleSuccessResponse,
        handleFailedResponse
    ]);

    const removeMessage = () => dispatch({ type: REMOVE_MESSAGE });

    const toggle2FaVerification = (
        value: boolean,
        authType?: AUTHENTICATION_TYPES
    ) =>
        dispatch({
            type: TOGGLE_2FA_VERIFICATION,
            payload: { value, authType: authType || null }
        });

    const toggle2FaEnable = (value: boolean, authType: AUTHENTICATION_TYPES) =>
        dispatch({ type: TOGGLE_2FA_ENABLE, payload: { value, authType } });

    const resetAuthType = useCallback(
        () => dispatch({ type: RESET_AUTH_TYPE }),
        []
    );

    const loginUser = useCallback(
        async (parameters: LoginUserParameters) => {
            const { payload, is2Fa, badRequestCallback, successCallback } =
                parameters;

            try {
                setLoading();

                const route = `${ENDPOINTS.Login}${is2Fa ? ENDPOINT_PATH_SEGMENTS.Tfa : ""}`;
                const { data } = await postData(route, payload);

                const is2FaRequired: boolean | undefined =
                    data.two_factor_auth_required;

                if (is2FaRequired) {
                    const authType: AUTHENTICATION_TYPES =
                        data.two_factor_auth_type;

                    toggle2FaVerification(true, authType);
                } else {
                    checkAuth();
                }

                successCallback?.();
            } catch (error) {
                const err = error as AxiosError;

                if (is2Fa && err.response?.status === 400) {
                    removeLoading();
                    badRequestCallback?.();
                } else {
                    handleFailedResponse(error);
                }
            }
        },
        [postData, handleFailedResponse, checkAuth]
    );

    const logoutUser = useCallback(
        async (hasNoApiCall?: boolean, message?: Message) => {
            try {
                setLoading();
                !hasNoApiCall && (await logout());

                dispatch({ type: AUTH_ERROR, payload: { message } });
                clearQueryParams();
                removeAlert();
                Sentry.setUser(null);
            } catch (error) {
                dispatch({ type: AUTH_ERROR });
                sessionStorage.clear();
                handleResponse(error);
            }
        },
        [logout, clearQueryParams, removeAlert, handleResponse]
    );

    const changeUserCompanyLevel = (newLevel: COMPANY_LEVELS) =>
        dispatch({ type: CHANGE_USER_COMPANY_LEVEL, payload: newLevel });

    const changeUserCustomResourceLimits = (
        settings: ResourceLimitSettings | null
    ) =>
        dispatch({
            type: CHANGE_USER_CUSTOM_RESOURCE_LIMITS,
            payload: settings
        });

    const setLoading = () => dispatch({ type: SET_LOADING });

    const removeLoading = () => dispatch({ type: REMOVE_LOADING });

    const setCookieConsent = (
        cookieName: COOKIE_NAMES,
        cookieValue: boolean
    ) => {
        dispatch({
            type: SET_COOKIE_CONSENT,
            payload: { cookieName, cookieValue }
        });
    };

    const value = useMemo(
        () => ({
            user: state.user,
            permissions: state.permissions,
            accessGroups: state.accessGroups,
            hasSso: state.hasSso,
            allCompanyLevelSettings: state.allCompanyLevelSettings,
            message: state.message,
            authType: state.authType,
            is2FaSetupRequired: state.is2FaSetupRequired,
            is2FaEnableFlowSeen: state.is2FaEnableFlowSeen,
            is2FaVerification: state.is2FaVerification,
            isAuthenticated: state.isAuthenticated,
            isSupportUser: state.isSupportUser,
            isAdminUser: state.isAdminUser,
            isAdminManagerUser: state.isAdminManagerUser,
            isInternalIntegrationUser: state.isInternalIntegrationUser,
            isSystemUser: state.isSystemUser,
            isSuperUser: state.isSuperUser,
            isCarrierUser: state.isCarrierUser,
            canInviteUsers: state.canInviteUsers,
            isLoading: state.isLoading,
            isAuthLoading: state.isAuthLoading,
            cookieConsent: state.cookieConsent,
            removeMessage,
            toggle2FaVerification,
            toggle2FaEnable,
            resetAuthType,
            checkAuth,
            loginUser,
            logoutUser,
            changeUserCompanyLevel,
            changeUserCustomResourceLimits,
            setCookieConsent
        }),
        [
            state.user,
            state.permissions,
            state.accessGroups,
            state.hasSso,
            state.allCompanyLevelSettings,
            state.message,
            state.authType,
            state.is2FaSetupRequired,
            state.is2FaEnableFlowSeen,
            state.is2FaVerification,
            state.isAuthenticated,
            state.isSupportUser,
            state.isAdminUser,
            state.isAdminManagerUser,
            state.isInternalIntegrationUser,
            state.isSystemUser,
            state.isSuperUser,
            state.isCarrierUser,
            state.canInviteUsers,
            state.isLoading,
            state.isAuthLoading,
            state.cookieConsent,
            resetAuthType,
            checkAuth,
            loginUser,
            logoutUser
        ]
    );

    return <AuthContext value={value}>{children}</AuthContext>;
};

export default AuthState;
export { PERMISSION_GROUP_ARRAY, getAllPermissions };
